import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ImagePlaceholder from '@/components/ui/ImagePlaceholder';
import Icon from '@/components/ui/AppIcon';
import { materialBadgeLabel, materialBadgeClass } from '@/lib/products/catalogueContent';
import type { Product } from '../productsData';

const CATEGORY_ICON: Record<string, string> = {
  plates: 'PlateIcon',
  bowls: 'BowlIcon',
  trays: 'TrayIcon',
  cups: 'CupIcon',
  cutlery: 'CutleryIcon',
  custom: 'SparklesIcon',
};

interface ProductCardProps {
  product: Product;
  /** Prioritise the image for above-the-fold cards. */
  priority?: boolean;
  /** Smaller variant used in the "You may also need" grid. */
  compact?: boolean;
}

// Catalogue family card: photo (or branded placeholder), material + size-count
// badges, serif family name and a "View details" affordance. The whole card is a
// single link to /products/[slug]; sizes, specs and CTAs live on the detail page.
export default function ProductCard({
  product,
  priority = false,
  compact = false,
}: ProductCardProps) {
  const hero = product.images?.[0]?.src ?? product.image;
  const heroAlt = product.images?.[0]?.alt || product.alt;
  const sizeCount = product.specs?.length || product.sizes?.length || 0;

  return (
    <Link
      href={`/products/${product.id}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-surface transition duration-200 ease-organic hover:-translate-y-1 hover:border-primary/20 hover:shadow-cinematic focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {/* Media */}
      <div className={`relative overflow-hidden ${compact ? 'aspect-[16/10]' : 'aspect-4/3'}`}>
        {hero ? (
          <div className="absolute inset-0 transition-transform duration-300 ease-out group-hover:scale-105">
            <AppImage
              src={hero}
              alt={heroAlt}
              fill
              priority={priority}
              sizes="(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 100vw"
              className="h-full w-full object-cover object-center"
            />
          </div>
        ) : (
          <ImagePlaceholder
            icon={CATEGORY_ICON[product.category ?? ''] ?? 'CubeIcon'}
            label="Photo placeholder"
          />
        )}

        {/* Material badge (top-left) */}
        <span
          className={`absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 font-cta text-xs font-medium ${materialBadgeClass(product)}`}
        >
          {materialBadgeLabel(product)}
        </span>

        {/* Size-count badge (top-right) */}
        {sizeCount > 0 && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/95 px-2.5 py-1 font-cta text-xs font-medium text-foreground shadow-sm">
            <Icon name="ClipboardDocumentListIcon" size={13} aria-hidden="true" />
            {sizeCount} {sizeCount === 1 ? 'size' : 'sizes'}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        <h3
          className={`font-headline font-bold leading-snug text-foreground ${compact ? 'text-base' : 'text-lg lg:text-xl'}`}
        >
          {product.title}
        </h3>
        <span className="mt-auto inline-flex items-center gap-1.5 pt-4 font-cta text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5">
          View details
          <Icon
            name="ArrowRightIcon"
            size={16}
            aria-hidden="true"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </span>
      </div>
    </Link>
  );
}
